/**
 * Pure geometry for the birth chart wheel (HORO-10360).
 *
 * Orientation is the standard astrological one: the Ascendant sits at 9
 * o'clock and the zodiac runs counter-clockwise from there. With no birth
 * time there is no Ascendant, so Aries 0 degrees takes its place.
 *
 * Angles are in degrees, math convention (0 = 3 o'clock, counter-clockwise
 * positive) and points are returned in SVG space (y grows downward).
 */

export const SIGN_ORDER = [
    'Aries',
    'Taurus',
    'Gemini',
    'Cancer',
    'Leo',
    'Virgo',
    'Libra',
    'Scorpio',
    'Sagittarius',
    'Capricorn',
    'Aquarius',
    'Pisces'
];

export const normalizeDegrees = (deg) => ((deg % 360) + 360) % 360;

/** Smallest separation between two longitudes, 0-180. */
export const angularDistance = (a, b) => {
    const diff = Math.abs(normalizeDegrees(a) - normalizeDegrees(b));
    return diff > 180 ? 360 - diff : diff;
};

/** Longitude that sits at 9 o'clock: the Ascendant, else Aries 0. */
export const getOriginLongitude = (placements) => {
    const houses = placements?.houses;
    if (Array.isArray(houses) && houses.length) {
        const first = houses.find((house) => house.number === 1);
        if (first) return first.cuspLongitude;
    }
    if (typeof placements?.ascendant?.longitude === 'number') return placements.ascendant.longitude;
    return 0;
};

/** Screen angle (math convention) of a zodiac longitude given the origin. */
export const longitudeToAngle = (longitude, origin = 0) => normalizeDegrees(180 + (longitude - origin));

export const polarToPoint = (cx, cy, radius, angleDeg) => {
    const rad = (angleDeg * Math.PI) / 180;
    return { x: cx + radius * Math.cos(rad), y: cy - radius * Math.sin(rad) };
};

/** Point on the wheel for a zodiac longitude. */
export const longitudeToPoint = (longitude, origin, cx, cy, radius) => polarToPoint(cx, cy, radius, longitudeToAngle(longitude, origin));

/** Middle of the arc from `from` to `to` (both longitudes, going forward). */
export const arcMidpoint = (from, to) => normalizeDegrees(from + normalizeDegrees(to - from) / 2);

/**
 * Give each body a radial `level` so planets within `minSeparation` degrees of
 * one another do not sit on top of each other. Level 0 is the base ring;
 * neighbours get the lowest level none of their close neighbours already use.
 * Returns bodies in the input order, each with `level` added.
 */
export const assignRadialLevels = (bodies, minSeparation = 8) => {
    const order = bodies.map((body, index) => ({ body, index })).sort((a, b) => a.body.longitude - b.body.longitude);
    const placed = [];
    order.forEach(({ body, index }) => {
        const usedLevels = new Set(
            placed.filter((other) => angularDistance(other.body.longitude, body.longitude) < minSeparation).map((other) => other.level)
        );
        let level = 0;
        while (usedLevels.has(level)) level += 1;
        placed.push({ body, index, level });
    });
    return placed.sort((a, b) => a.index - b.index).map(({ body, level }) => ({ ...body, level }));
};

/** Sign index (0 = Aries) for a longitude. */
export const signIndexForLongitude = (longitude) => Math.floor(normalizeDegrees(longitude) / 30);

/** Ring positions for the 12 zodiac glyphs: the middle of each 30 degree sign. */
export const getSignLabelAngles = (origin) => SIGN_ORDER.map((name, index) => ({ name, angle: longitudeToAngle(index * 30 + 15, origin) }));

/**
 * Where to write each house number: the middle of the house, or of a 30 degree
 * slice from the origin when houses are unknown (callers skip that case).
 */
export const getHouseLabels = (houses, origin) => {
    if (!Array.isArray(houses) || !houses.length) return [];
    const sorted = [...houses].sort((a, b) => a.number - b.number);
    return sorted.map((house, index) => {
        const next = sorted[(index + 1) % sorted.length];
        return {
            number: house.number,
            angle: longitudeToAngle(arcMidpoint(house.cuspLongitude, next.cuspLongitude), origin)
        };
    });
};

/** First sentence of a block of copy ('A. B.' -> 'A.'); the whole text when it has no sentence end. */
export const firstSentence = (text) => {
    const trimmed = (text || '').trim();
    const match = trimmed.match(/^[\s\S]*?[.!?]+(?=\s|$)/);
    return match ? match[0] : trimmed;
};

export const capitalize = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : '');

export const ordinal = (n) => {
    const mod100 = n % 100;
    if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
    switch (n % 10) {
        case 1:
            return `${n}st`;
        case 2:
            return `${n}nd`;
        case 3:
            return `${n}rd`;
        default:
            return `${n}th`;
    }
};

/** 'YYYY-MM-DD' -> 'March 8, 2003' (no timezone shifting). */
export const formatBirthDate = (isoDate) => {
    if (!isoDate) return '';
    const date = new Date(`${isoDate}T00:00:00Z`);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};
